import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSettings, updateSettings, uploadLogo, deleteLogo, geocodeAddress } from '../services/settingsService';
import { getTeamMembers, addTeamMember, updateTeamMember, removeTeamMember, updateSmsNotifications } from '../services/teamService';
import { toggleLocationSubscription, createCheckoutSession, createPortalSession } from '../services/billingService';
import { getMeters, createMeter, updateMeter, deleteMeter, getApiToken, regenerateApiToken } from '../services/waterService';
import { getSwiperMappings, saveSwiperMappings, deleteSwiperMapping } from '../services/plService';
import { unitOptions } from '../utils/unitNames';
import { useLocationContext } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

const Settings = () => {
  const navigate = useNavigate();
  const { needsSetup, refreshAuth, signOut } = useAuth();
  const { currentLocation, refreshLocations } = useLocationContext();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoKey, setLogoKey] = useState(Date.now());
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, [currentLocation?.id]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await getSettings();
      setFormData(data);
    } catch (err) {
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSuccess('');
  };

  const handleNumberChange = (field, value) => {
    const num = value === '' ? '' : parseInt(value, 10);
    setFormData(prev => ({ ...prev, [field]: num }));
    setSuccess('');
  };

  const handleVendingNameChange = (index, value) => {
    const names = [...formData.vending_machine_names];
    names[index] = value;
    setFormData(prev => ({ ...prev, vending_machine_names: names }));
    setSuccess('');
  };

  const addVendingMachine = () => {
    setFormData(prev => ({
      ...prev,
      vending_machine_names: [...prev.vending_machine_names, '']
    }));
    setSuccess('');
  };

  const removeVendingMachine = (index) => {
    const names = formData.vending_machine_names.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, vending_machine_names: names }));
    setSuccess('');
  };

  const handleSave = async () => {
    const wasSetup = needsSetup;
    if (wasSetup) {
      const hasEquipment = (formData.num_ss_bays || 0) > 0
        || (formData.num_auto_washes || 0) > 0
        || (formData.num_vacuums || 0) > 0
        || (formData.num_changers || 0) > 0
        || (formData.num_dog_washes || 0) > 0
        || (formData.vending_machine_names?.length || 0) > 0;
      if (!hasEquipment) {
        setError('Please add at least one piece of equipment before continuing.');
        return;
      }
    }
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const data = await updateSettings(formData);
      setFormData(data);
      await refreshLocations();
      if (wasSetup) {
        await refreshAuth();
        navigate('/');
        return;
      }
      setSuccess('Settings saved successfully');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save settings';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      setUploadingLogo(true);
      setError('');
      setSuccess('');
      const data = await uploadLogo(file);
      setFormData(data);
      setLogoKey(Date.now());
      setSuccess('Logo uploaded successfully');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to upload logo';
      setError(msg);
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleLogoDelete = async () => {
    try {
      setError('');
      setSuccess('');
      const data = await deleteLogo();
      setFormData(data);
      setLogoKey(Date.now());
      setSuccess('Logo removed — using default');
    } catch (err) {
      setError('Failed to remove logo');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error || 'Failed to load settings'}
      </div>
    );
  }

  return (
    <div>
      {needsSetup && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-5">
          <h2 className="text-lg font-semibold text-blue-900">Welcome to Car Wash Copilot!</h2>
          <p className="mt-1 text-sm text-blue-700">
            Let's set up your first wash location. Enter the equipment you have below — how many self-serve bays, vacuums, changers, automatic washes, and vending machines. You can always update these later.
          </p>
          <p className="mt-2 text-sm text-blue-700">
            Fill in at least one equipment type, then hit <strong>Save & Continue to Dashboard</strong> to get started.
          </p>
        </div>
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure settings for {currentLocation?.business_name || 'your car wash'}
        </p>
      </div>

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="space-y-6">
        {/* Business Info */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Business Info</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
              <input
                type="text"
                value={formData.business_name}
                onChange={(e) => handleChange('business_name', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                maxLength={200}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
              <input
                type="text"
                value={formData.street_address || ''}
                onChange={(e) => handleChange('street_address', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                maxLength={200}
                placeholder="123 Main St"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <input
                  type="text"
                  value={formData.city || ''}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  maxLength={100}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <input
                  type="text"
                  value={formData.state || ''}
                  onChange={(e) => handleChange('state', e.target.value.toUpperCase())}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  maxLength={2}
                  placeholder="PA"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                <input
                  type="text"
                  value={formData.zip_code || ''}
                  onChange={(e) => handleChange('zip_code', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  maxLength={10}
                  placeholder="19601"
                />
              </div>
            </div>

            {/* Timezone — used everywhere the server compares dates to
                "today", so schedule due-date labels match the operator's
                local calendar instead of UTC. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
              <select
                value={formData.timezone || 'America/New_York'}
                onChange={(e) => handleChange('timezone', e.target.value)}
                className="w-full sm:w-72 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="America/New_York">Eastern (America/New_York)</option>
                <option value="America/Chicago">Central (America/Chicago)</option>
                <option value="America/Denver">Mountain (America/Denver)</option>
                <option value="America/Phoenix">Arizona (America/Phoenix, no DST)</option>
                <option value="America/Los_Angeles">Pacific (America/Los_Angeles)</option>
                <option value="America/Anchorage">Alaska (America/Anchorage)</option>
                <option value="Pacific/Honolulu">Hawaii (Pacific/Honolulu)</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Determines the "today" the app uses for due-date math. Change this if you're outside the US.
              </p>
            </div>

            {/* GPS coordinates — used to verify staff clock-in location */}
            <div>
              <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
                <label className="block text-sm font-medium text-gray-700">GPS Location <span className="text-gray-400 font-normal">(optional)</span></label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={async () => {
                      setError('');
                      try {
                        const res = await geocodeAddress({
                          street_address: formData.street_address || '',
                          city: formData.city || '',
                          state: formData.state || '',
                          zip_code: formData.zip_code || '',
                        });
                        handleChange('latitude', Number(res.latitude.toFixed(6)));
                        handleChange('longitude', Number(res.longitude.toFixed(6)));
                      } catch (err) {
                        setError(err.response?.data?.error || 'Address lookup failed');
                      }
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Look up from address
                  </button>
                  <span className="text-xs text-gray-300">·</span>
                  <button
                    type="button"
                    onClick={() => {
                      if (!navigator.geolocation) {
                        setError('Your browser does not support GPS. Enter coordinates manually.');
                        return;
                      }
                      navigator.geolocation.getCurrentPosition(
                        (pos) => {
                          handleChange('latitude', Number(pos.coords.latitude.toFixed(6)));
                          handleChange('longitude', Number(pos.coords.longitude.toFixed(6)));
                        },
                        () => setError('Could not read GPS. Grant location permission or enter coordinates manually.'),
                        { enableHighAccuracy: true, timeout: 10000 },
                      );
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Use current position
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input
                  type="number"
                  step="0.000001"
                  value={formData.latitude ?? ''}
                  onChange={(e) => handleChange('latitude', e.target.value === '' ? null : Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Latitude, e.g. 40.312403"
                />
                <input
                  type="number"
                  step="0.000001"
                  value={formData.longitude ?? ''}
                  onChange={(e) => handleChange('longitude', e.target.value === '' ? null : Number(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Longitude, e.g. -75.949117"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Used to verify staff GPS clock-in. Coordinates auto-populate from the street address when you save. Or tap "Look up from address" to preview, or "Use current position" while standing at the wash. Leave blank to disable on-site verification.
              </p>
            </div>
          </div>
        </div>

        {/* Logo */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Logo</h2>
          <div className="flex items-start space-x-6">
            <div className="flex-shrink-0">
              <div className="h-16 w-40 bg-gray-100 rounded-md flex items-center justify-center overflow-hidden border border-gray-200">
                {formData.has_custom_logo ? (
                  <img
                    key={logoKey}
                    src={`${config.API_BASE_URL}/settings/logo?t=${logoKey}`}
                    alt="Logo"
                    className="h-full object-contain"
                  />
                ) : (
                  <span className="text-sm text-gray-400">Default logo</span>
                )}
              </div>
            </div>
            <div className="flex-1">
              <label className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  disabled={uploadingLogo}
                  className="hidden"
                />
              </label>
              {formData.has_custom_logo && (
                <button
                  type="button"
                  onClick={handleLogoDelete}
                  className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-red-600 bg-white hover:bg-red-50"
                >
                  Remove
                </button>
              )}
              <p className="mt-2 text-xs text-gray-500">
                PNG, JPG, SVG, or WebP. Max 5 MB. Displayed in the navbar.
              </p>
            </div>
          </div>
        </div>

        {/* Equipment */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Equipment</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SS Bays</label>
              <input
                type="number"
                min="0"
                max="50"
                value={formData.num_ss_bays}
                onChange={(e) => handleNumberChange('num_ss_bays', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Auto Washes</label>
              <input
                type="number"
                min="0"
                max="10"
                value={formData.num_auto_washes}
                onChange={(e) => handleNumberChange('num_auto_washes', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vacuums</label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.num_vacuums}
                onChange={(e) => handleNumberChange('num_vacuums', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Changers</label>
              <input
                type="number"
                min="0"
                max="20"
                value={formData.num_changers}
                onChange={(e) => handleNumberChange('num_changers', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dog Washes</label>
              <input
                type="number"
                min="0"
                max="20"
                value={formData.num_dog_washes}
                onChange={(e) => handleNumberChange('num_dog_washes', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Leave at 0 if you don't have one — the section stays hidden.
              </p>
            </div>
          </div>
        </div>

        {/* Vending Machines */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Vending Machines
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({formData.vending_machine_names.length})
            </span>
          </h2>
          <div className="space-y-2">
            {formData.vending_machine_names.map((name, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => handleVendingNameChange(index, e.target.value)}
                  placeholder={`Machine ${index + 1}`}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={() => removeVendingMachine(index)}
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md"
                  title="Remove"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addVendingMachine}
            className="mt-3 inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Machine
          </button>
        </div>

        {/* Cash Counting Defaults */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Cash Counting Defaults</h2>
          <p className="text-sm text-gray-500 mb-4">
            Set defaults for new cash outs. Pooled mode uses a single total instead of counting each unit individually.
          </p>
          <div className="space-y-3">
            {[
              { field: 'default_ss_pooled', label: 'Pool SS Bay cash' },
              { field: 'default_vac_pooled', label: 'Pool Vacuum cash' },
              { field: 'default_vending_pooled', label: 'Pool Vending cash' },
              { field: 'default_changer_pooled', label: 'Pool Changer cash' },
              ...((formData.num_auto_washes || 0) > 1
                ? [{ field: 'default_auto_pooled', label: 'Pool Auto Wash cash' }]
                : []),
              ...((formData.num_dog_washes || 0) > 1
                ? [{ field: 'default_dog_pooled', label: 'Pool Dog Wash cash' }]
                : []),
            ].map(({ field, label }) => (
              <label key={field} className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData[field] || false}
                  onChange={(e) => handleChange(field, e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Financial */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Financial</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quarters per Bucket</label>
              <div className="flex items-center space-x-3">
                <input
                  type="number"
                  min="1"
                  max="100000"
                  value={formData.quarters_per_bucket}
                  onChange={(e) => handleNumberChange('quarters_per_bucket', e.target.value)}
                  className="w-48 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">
                  = ${(formData.quarters_per_bucket * 0.25).toFixed(2)}
                </span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
              <div className="flex items-center space-x-3">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.tax_rate ?? 6}
                  onChange={(e) => setFormData(prev => ({ ...prev, tax_rate: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                  className="w-48 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Operating Defaults */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Operating Defaults</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Date Range (days)</label>
              <input
                type="number"
                min="1"
                max="365"
                value={formData.default_date_range_days}
                onChange={(e) => handleNumberChange('default_date_range_days', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : needsSetup ? 'Save & Continue to Dashboard' : 'Save Settings'}
        </button>
      </div>

      {/* Billing */}
      {!needsSetup && <div className="mt-8"><Billing /></div>}

      {/* Location Subscriptions */}
      {!needsSetup && <div className="mt-8"><LocationSubscriptions /></div>}

      {/* Team Management */}
      <div className="mt-8">
        <TeamManagement />
      </div>

      {/* Water Meters */}
      {!needsSetup && <div className="mt-8"><WaterMeters /></div>}

      {/* CryptoPay swiper mappings — per-location overrides for the
          CSV SwiperID → equipment mapping used during CC upload. */}
      {!needsSetup && <div className="mt-8"><SwiperMappings /></div>}

      {/* Account */}
      {!needsSetup && (
        <div className="mt-8 bg-white rounded-lg shadow p-4 sm:p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Account</h2>
          <p className="text-sm text-gray-500 mb-4">
            Sign out on this device. You can sign back in with your phone number anytime.
          </p>
          <button
            onClick={() => {
              if (window.confirm('Sign out of Car Wash Copilot?')) signOut();
            }}
            className="px-4 py-2 border border-red-300 text-red-700 hover:bg-red-50 rounded-md text-sm font-medium"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

const BILLING_STATUS_LABELS = {
  active: 'Active',
  trialing: 'Free trial',
  past_due: 'Payment past due',
  canceled: 'Canceled',
  expired: 'Expired',
};

const Billing = () => {
  const { account, membership, refreshAuth } = useAuth();
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Stripe's webhook can land a few seconds after Checkout redirects back,
  // so re-read the account once more after a short wait.
  useEffect(() => {
    const result = new URLSearchParams(window.location.search).get('billing');
    if (result === 'success') {
      setNotice('Thanks! Your payment went through. It can take a few seconds for your subscription to show as active.');
      refreshAuth();
      const timer = setTimeout(refreshAuth, 5000);
      return () => clearTimeout(timer);
    }
    if (result === 'canceled') setNotice('Checkout was canceled. You have not been charged.');
  }, [refreshAuth]);

  if (!account || !['owner', 'admin'].includes(membership?.role)) return null;

  const status = account.subscription_status;
  // Mirrors the checkout endpoint's guard against a second subscription.
  const subscribed = account.has_subscription && ['active', 'trialing', 'past_due'].includes(status);

  const redirect = async (createSession) => {
    setRedirecting(true);
    setError('');
    try {
      const { url } = await createSession();
      window.location.href = url;
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to open billing. Please contact help@carwashcopilot.com');
      setRedirecting(false);
    }
  };

  let detail = '';
  if (status === 'trialing') {
    detail = account.trial_days_remaining == null
      ? 'Your account is on a free trial with no end date.'
      : `Your free trial ends in ${account.trial_days_remaining} day${account.trial_days_remaining !== 1 ? 's' : ''}.`;
  } else if (status === 'past_due') {
    detail = 'Your last payment failed. Update your payment method to keep access.';
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Billing</h2>
      <p className="text-sm text-gray-700">
        Status: <span className="font-medium">{BILLING_STATUS_LABELS[status] || status}</span>
      </p>
      {detail && <p className="text-sm text-gray-500 mt-1">{detail}</p>}
      {notice && <div className="mt-3 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded text-sm">{notice}</div>}
      {error && <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>}
      <div className="mt-4 flex flex-wrap gap-3">
        {!subscribed && (
          <button
            onClick={() => redirect(createCheckoutSession)}
            disabled={redirecting}
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md text-sm font-medium disabled:opacity-50"
          >
            {redirecting ? 'Redirecting...' : 'Subscribe'}
          </button>
        )}
        {account.has_billing_account && (
          <button
            onClick={() => redirect(createPortalSession)}
            disabled={redirecting}
            className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md text-sm font-medium disabled:opacity-50"
          >
            Manage billing
          </button>
        )}
      </div>
    </div>
  );
};

const LocationSubscriptions = () => {
  const { locations, refreshLocations } = useLocationContext();
  const { account } = useAuth();
  const [toggling, setToggling] = useState(null);
  const [error, setError] = useState('');

  if (locations.length <= 1) return null; // No need to show for single location

  const handleToggle = async (loc) => {
    const newValue = !loc.subscribed;
    setToggling(loc.id);
    setError('');
    try {
      await toggleLocationSubscription(loc.id, newValue);
      await refreshLocations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update subscription');
    } finally {
      setToggling(null);
    }
  };

  // Billing is per account, so only list locations this account actually owns.
  // Superadmins get every location in the database from GET /locations, which
  // previously made this section show locations the billing guard couldn't see.
  const billableLocations = account?.id
    ? locations.filter((l) => l.account_id === account.id)
    : locations;
  const subscribedCount = billableLocations.filter(l => l.subscribed !== false).length;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Location Subscriptions</h2>
        <p className="text-sm text-gray-500 mt-1">
          Choose which locations are included in your subscription. You are billed per subscribed location.
          {account?.subscription_status === 'active' && ` Currently subscribing for ${subscribedCount} location${subscribedCount !== 1 ? 's' : ''}.`}
        </p>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>}

      <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
        {billableLocations.map((loc) => (
          <div key={loc.id} className="flex items-center justify-between p-4">
            <div>
              <p className="text-sm font-medium text-gray-900">{loc.business_name}</p>
              {loc.street_address && (
                <p className="text-xs text-gray-500">
                  {loc.street_address}{loc.city ? `, ${loc.city}` : ''}{loc.state ? ` ${loc.state}` : ''}
                </p>
              )}
              {loc.subscribed === false && loc.unsubscribed_at && (
                <p className="text-xs text-red-500 mt-0.5">
                  Data will be deleted {new Date(new Date(loc.unsubscribed_at).getTime() + 180 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </p>
              )}
            </div>
            <button
              onClick={() => handleToggle(loc)}
              disabled={toggling === loc.id}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                loc.subscribed !== false ? 'bg-blue-600' : 'bg-gray-300'
              } ${toggling === loc.id ? 'opacity-50' : ''}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                loc.subscribed !== false ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const PAGE_OPTIONS = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'pl', label: 'P&L' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'maintenance', label: 'Maintenance' },
];

const TeamManagement = () => {
  const { user, account, membership, refreshAuth } = useAuth();
  const isOwner = membership?.role === 'owner';
  const { currentLocation, locations } = useLocationContext();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ display_name: '', phone_number: '', role: 'member', page_permissions: null, hourly_wage: '', position: '', active: true, assigned_location_ids: [] });
  const [invitePhone, setInvitePhone] = useState(null);
  const [smsEnabled, setSmsEnabled] = useState(account?.sms_notifications_enabled !== false);
  const [smsToggling, setSmsToggling] = useState(false);

  useEffect(() => { fetchMembers(); }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const data = await getTeamMembers();
      setMembers(data.members || []);
    } catch (err) {
      setError('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ display_name: '', phone_number: '', role: 'member', page_permissions: null, hourly_wage: '', position: '', active: true, assigned_location_ids: [] });
    setShowAddForm(false);
    setEditingId(null);
  };

  const handleAdd = async () => {
    setError('');
    setSuccess('');
    if (!formData.display_name.trim() || !formData.phone_number.trim()) {
      setError('Name and phone number are required');
      return;
    }
    try {
      const phone = formData.phone_number.trim();
      await addTeamMember({
        ...formData,
        hourly_wage: formData.hourly_wage === '' ? null : Number(formData.hourly_wage),
      });
      setSuccess('Team member added');
      setInvitePhone(phone);
      resetForm();
      fetchMembers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add team member');
    }
  };

  const handleUpdate = async () => {
    setError('');
    setSuccess('');
    try {
      await updateTeamMember(editingId, {
        role: formData.role,
        page_permissions: formData.page_permissions,
        display_name: formData.display_name,
        hourly_wage: formData.hourly_wage === '' ? null : Number(formData.hourly_wage),
        position: formData.position,
        assigned_location_ids: formData.assigned_location_ids,
        active: formData.active,
      });
      setSuccess('Team member updated');
      resetForm();
      fetchMembers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update team member');
    }
  };

  const handleRemove = async (id, name) => {
    if (!window.confirm(`Remove ${name} from your team?`)) return;
    setError('');
    setSuccess('');
    try {
      await removeTeamMember(id);
      setSuccess('Team member removed');
      fetchMembers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove team member');
    }
  };

  const toggleDuplicateAlerts = async (member) => {
    setError('');
    const next = !member.receives_duplicate_alerts_effective;
    try {
      await updateTeamMember(member.id, { receives_duplicate_alerts: next });
      fetchMembers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update alert settings');
    }
  };

  const startEdit = (member) => {
    setEditingId(member.id);
    setShowAddForm(false);
    setFormData({
      display_name: member.user?.display_name || '',
      phone_number: member.user?.phone_number || '',
      role: member.role,
      page_permissions: member.page_permissions || null,
      hourly_wage: member.hourly_wage != null ? String(member.hourly_wage) : '',
      position: member.position || '',
      active: member.active !== false,
      assigned_location_ids: member.assigned_location_ids || [],
    });
  };

  const toggleActive = async (member) => {
    setError('');
    try {
      await updateTeamMember(member.id, { active: !member.active });
      fetchMembers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update active status');
    }
  };

  const togglePermission = (key) => {
    setFormData(prev => {
      const perms = prev.page_permissions ? { ...prev.page_permissions } : {};
      if (perms[key] === false) {
        delete perms[key];
      } else {
        perms[key] = false;
      }
      return { ...prev, page_permissions: Object.keys(perms).length > 0 ? perms : null };
    });
  };

  const handleSmsToggle = async () => {
    const newValue = !smsEnabled;
    setSmsToggling(true);
    try {
      await updateSmsNotifications(newValue);
      setSmsEnabled(newValue);
      refreshAuth();
    } catch (err) {
      setError('Failed to update SMS notification setting');
    } finally {
      setSmsToggling(false);
    }
  };

  const roleBadge = (role) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) return <div className="text-center py-4 text-gray-500">Loading team...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Team Members</h2>
        {!showAddForm && !editingId && (
          <button
            onClick={() => { setShowAddForm(true); resetForm(); setShowAddForm(true); }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            + Add Member
          </button>
        )}
      </div>

      {/* SMS Notifications Toggle */}
      <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-900">SMS Notifications</p>
          <p className="text-xs text-gray-500">Send text messages to team members when work orders are assigned to them</p>
        </div>
        <button
          onClick={handleSmsToggle}
          disabled={smsToggling}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${smsEnabled ? 'bg-blue-600' : 'bg-gray-300'} ${smsToggling ? 'opacity-50' : ''}`}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${smsEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded text-sm">{success}</div>}

      {invitePhone && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <p className="text-sm text-green-800">An invite text has been sent to {invitePhone}.</p>
          <button
            onClick={() => setInvitePhone(null)}
            className="text-green-600 hover:text-green-800 text-sm font-medium px-2"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Add / Edit Form */}
      {(showAddForm || editingId) && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {editingId ? 'Edit Member' : 'Add New Member'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="John Smith"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input
                type="tel"
                value={formData.phone_number}
                onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                disabled={!!editingId}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="(555) 123-4567"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
              className="w-full sm:w-48 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
              {/* New members start as admin/member; owners can promote once added */}
              {isOwner && editingId && <option value="owner">Owner</option>}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Position (optional)</label>
              <input
                type="text"
                value={formData.position}
                onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. Attendant, Tech, Manager"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hourly wage (optional)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.hourly_wage}
                  onChange={(e) => setFormData(prev => ({ ...prev, hourly_wage: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md pl-7 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="15.00"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Used for timesheet labor cost. OT (over 40 hrs/week) is paid at 1.5×.</p>
            </div>
          </div>

          {/* Location access — only meaningful with more than one location, and
              owners always have access to all of them. */}
          {(locations || []).length > 1 && formData.role !== 'owner' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location access</label>
              <p className="text-xs text-gray-500 mb-2">
                Leave all unchecked for access to every location. Check specific
                locations to limit this person to those only.
              </p>
              <div className="space-y-2">
                {locations.map((loc) => {
                  const checked = formData.assigned_location_ids.includes(loc.id);
                  return (
                    <label key={loc.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setFormData((prev) => ({
                          ...prev,
                          assigned_location_ids: e.target.checked
                            ? [...prev.assigned_location_ids, loc.id]
                            : prev.assigned_location_ids.filter((id) => id !== loc.id),
                        }))}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{loc.business_name}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {formData.assigned_location_ids.length === 0
                  ? 'Currently: all locations'
                  : `Currently: ${formData.assigned_location_ids.length} of ${locations.length} locations`}
              </p>
            </div>
          )}
          {editingId && (
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              Active employee — counted in timesheet and labor cost
            </label>
          )}
          {formData.role === 'member' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Page Access</label>
              <div className="flex flex-wrap gap-3">
                {PAGE_OPTIONS.map(opt => {
                  const blocked = formData.page_permissions?.[opt.key] === false;
                  return (
                    <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!blocked}
                        onChange={() => togglePermission(opt.key)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-1">Uncheck pages to hide them from this user. Admins always have full access.</p>
            </div>
          )}
          {/* SMS consent notice — shown only when adding (phone can't change on edit).
              Must match the mockup on /messaging-consent for Twilio toll-free compliance. */}
          {!editingId && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900">
              <p className="font-semibold mb-1">SMS consent notice</p>
              <p>
                By clicking <strong>Add Member</strong>, you confirm that this team
                member has agreed to receive SMS messages from Car Wash Copilot for
                account setup and operational notifications. Message &amp; data rates
                may apply. Message frequency varies. Reply STOP to opt out, HELP for
                help. See our{' '}
                <a
                  href="/messaging-consent"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-700"
                >
                  messaging terms
                </a>
                .
              </p>
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={editingId ? handleUpdate : handleAdd}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              {editingId ? 'Save Changes' : 'Add Member'}
            </button>
            <button
              onClick={resetForm}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Members List */}
      <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
        {members.length === 0 ? (
          <div className="p-6 text-center text-gray-500">No team members yet.</div>
        ) : (
          members.map(member => {
            const isCurrentUser = member.user_id === user?.id;
            const perms = member.page_permissions;
            const hiddenPages = perms ? PAGE_OPTIONS.filter(p => perms[p.key] === false).map(p => p.label) : [];
            return (
              <div key={member.id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">
                      {member.user?.display_name || 'Unnamed'}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${roleBadge(member.role)}`}>
                      {member.role}
                    </span>
                    {member.position && (
                      <span className="text-xs text-gray-500">{member.position}</span>
                    )}
                    {member.active === false && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">inactive</span>
                    )}
                    {member.restricted_to_locations && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"
                        title={`Limited to ${member.assigned_location_ids.length} location${member.assigned_location_ids.length === 1 ? '' : 's'}`}
                      >
                        {member.assigned_location_ids.length} of {(locations || []).length} locations
                      </span>
                    )}
                    {isCurrentUser && (
                      <span className="text-xs text-gray-400">(you)</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {member.user?.phone_number}
                    {member.hourly_wage != null && (
                      <span className="ml-2 text-gray-400">· ${Number(member.hourly_wage).toFixed(2)}/hr</span>
                    )}
                  </div>
                  {hiddenPages.length > 0 && (
                    <div className="text-xs text-orange-600 mt-0.5">
                      Hidden: {hiddenPages.join(', ')}
                    </div>
                  )}
                  {member.user?.firebase_uid?.startsWith('pending:') && (
                    <div className="text-xs text-yellow-600 mt-0.5">Hasn't logged in yet</div>
                  )}
                  <label
                    className="text-xs text-gray-600 mt-1 inline-flex items-center gap-1.5 cursor-pointer"
                    title="Receive in-app alerts for possible duplicate credit-card charges. Default: on for owners/admins, off for members."
                  >
                    <input
                      type="checkbox"
                      checked={!!member.receives_duplicate_alerts_effective}
                      onChange={() => toggleDuplicateAlerts(member)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    Duplicate-charge alerts
                  </label>
                </div>
                {!isCurrentUser && (member.role !== 'owner' || isOwner) && (
                  <div className="flex gap-2 items-center">
                    <button
                      onClick={() => toggleActive(member)}
                      className={`text-xs font-medium px-2 py-1 rounded ${member.active === false ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                      title={member.active === false ? 'Reactivate this employee' : 'Mark as inactive (preserves history)'}
                    >
                      {member.active === false ? 'Reactivate' : 'Active'}
                    </button>
                    <button
                      onClick={() => startEdit(member)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleRemove(member.id, member.user?.display_name)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const WATER_METER_CATEGORIES = [
  { value: 'main', label: 'Main line' },
  { value: 'auto_wash', label: 'Auto Wash' },
  { value: 'ss_bay', label: 'SS Bay(s)' },
  { value: 'other', label: 'Other' },
];

const WaterMeters = () => {
  const { currentLocation } = useLocationContext();
  const [meters, setMeters] = useState([]);
  const [apiToken, setApiToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // meter being edited, or {} for new

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [m, t] = await Promise.all([getMeters(), getApiToken()]);
      setMeters(m.meters || []);
      setApiToken(t.api_token || '');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load water meters');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation?.id]);

  const handleSave = async (payload) => {
    try {
      if (payload.id) {
        await updateMeter(payload.id, payload);
      } else {
        await createMeter(payload);
      }
      setEditing(null);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save meter');
    }
  };

  const handleDelete = async (meter) => {
    if (!window.confirm(`Delete "${meter.name}"? All its readings will be removed. This cannot be undone.`)) return;
    try {
      await deleteMeter(meter.id);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete meter');
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Regenerate the API token? Any edge devices using the old token will stop working until they\'re updated.')) return;
    try {
      const t = await regenerateApiToken();
      setApiToken(t.api_token || '');
      setShowToken(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to regenerate token');
    }
  };

  const copyToken = () => {
    if (!apiToken) return;
    navigator.clipboard.writeText(apiToken).then(() => {
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    });
  };

  if (loading) return <div className="text-center py-4 text-gray-500">Loading water meters...</div>;

  const categoryLabel = (v) => WATER_METER_CATEGORIES.find(c => c.value === v)?.label || v;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Water Meters</h2>
        {!editing && (
          <button
            onClick={() => setEditing({ name: '', category: 'main', device_serial: '', active: true })}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            + Add Meter
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>}

      {/* API token block */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900">Edge Device API Token</p>
            <p className="text-xs text-gray-500">Used by the water-bridge script on the Raspberry Pi (or similar) to POST readings. Treat like a password.</p>
          </div>
          <button
            onClick={handleRegenerate}
            className="text-red-600 hover:bg-red-50 border border-red-200 px-3 py-1.5 rounded text-xs font-medium flex-shrink-0"
          >
            Regenerate
          </button>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <input
            type={showToken ? 'text' : 'password'}
            value={apiToken}
            readOnly
            className="flex-1 border border-gray-300 rounded px-3 py-1.5 text-xs font-mono bg-gray-50"
          />
          <button
            onClick={() => setShowToken((v) => !v)}
            className="text-xs text-gray-600 hover:text-gray-900 px-2 py-1.5"
          >
            {showToken ? 'Hide' : 'Show'}
          </button>
          <button
            onClick={copyToken}
            className="bg-gray-800 hover:bg-gray-900 text-white px-3 py-1.5 rounded text-xs font-medium"
          >
            {tokenCopied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Add / Edit form */}
      {editing && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{editing.id ? 'Edit Meter' : 'New Meter'}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="e.g. Main, Auto Wash, SS Header"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={editing.category}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {WATER_METER_CATEGORIES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Device serial (optional)</label>
            <input
              type="text"
              value={editing.device_serial || ''}
              onChange={(e) => setEditing({ ...editing, device_serial: e.target.value })}
              placeholder="Droplet serial — lets the edge script auto-match"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={editing.active !== false}
              onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded"
            />
            Active — accepting readings from the edge device
          </label>
          <div className="flex gap-3">
            <button
              onClick={() => {
                if (!editing.name.trim()) { setError('Name is required'); return; }
                handleSave({ ...editing, name: editing.name.trim() });
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
            >
              {editing.id ? 'Save' : 'Add Meter'}
            </button>
            <button
              onClick={() => setEditing(null)}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Meters list */}
      <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
        {meters.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-sm">
            No water meters yet. Add one for each Droplet you install (main line, Auto Wash, etc.).
          </div>
        ) : (
          meters.map((m) => (
            <div key={m.id} className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">{m.name}</span>
                  <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{categoryLabel(m.category)}</span>
                  {!m.active && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">inactive</span>}
                </div>
                {m.device_serial && (
                  <div className="text-xs text-gray-500 font-mono mt-0.5">{m.device_serial}</div>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => setEditing({ ...m })}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(m)}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// ─── CryptoPay Swiper Mappings ──────────────────────────────────────────
// Per-location overrides for how a raw CryptoPay SwiperID maps to a
// canonical unit name. Backfills the ones the built-in regex can't
// handle (Sinky calls its auto swiper "Auto" not "Automatic"; vacuum
// swipers don't have a naming convention we can regex against).

const SwiperMappings = () => {
  const { currentLocation } = useLocationContext();
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newRow, setNewRow] = useState(null); // {swiper_id, unit_name}
  const [savingIds, setSavingIds] = useState(new Set());
  const options = React.useMemo(() => unitOptions(currentLocation), [currentLocation]);

  const fetchMappings = async () => {
    try {
      setLoading(true);
      const data = await getSwiperMappings();
      setMappings(data.mappings || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation?.id]);

  const updateRow = (id, patch) => {
    setMappings((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };

  const saveRow = async (row) => {
    setSavingIds((prev) => new Set(prev).add(row.id));
    setError('');
    try {
      await saveSwiperMappings([{
        swiper_id: row.swiper_id,
        unit_name: row.unit_name,
        active: row.active,
      }], { remap: true });
      await fetchMappings();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSavingIds((prev) => { const next = new Set(prev); next.delete(row.id); return next; });
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`Delete mapping for "${row.swiper_id}"? Historical transactions will fall back to the built-in matching rules.`)) return;
    try {
      await deleteSwiperMapping(row.id);
      await fetchMappings();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const addNew = async () => {
    if (!newRow?.swiper_id?.trim() || !newRow?.unit_name?.trim()) {
      setError('SwiperID and unit are both required');
      return;
    }
    setError('');
    try {
      await saveSwiperMappings([{
        swiper_id: newRow.swiper_id.trim(),
        unit_name: newRow.unit_name,
        active: true,
      }], { remap: true });
      setNewRow(null);
      await fetchMappings();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    }
  };

  if (loading) return <div className="text-center py-4 text-gray-500">Loading swiper mappings...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">CryptoPay Swiper Mappings</h2>
          <p className="text-xs text-gray-500 mt-1">
            Tell the app how raw CryptoPay SwiperIDs should map to your equipment.
            Only needed when the built-in matcher can't figure it out (e.g. a swiper
            configured as "Auto" instead of "Automatic", or a vacuum with a custom name).
            After saving, historical transactions with these swipers are re-mapped
            automatically.
          </p>
        </div>
        {!newRow && (
          <button
            onClick={() => setNewRow({ swiper_id: '', unit_name: options[0]?.value || 'Other' })}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            + Add Mapping
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>}

      {newRow && (
        <div className="bg-white p-4 rounded-lg shadow space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">SwiperID (as it appears in the CSV)</label>
              <input
                type="text"
                value={newRow.swiper_id}
                onChange={(e) => setNewRow({ ...newRow, swiper_id: e.target.value })}
                placeholder="e.g. Auto, Vac1"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Map to</label>
              <select
                value={newRow.unit_name}
                onChange={(e) => setNewRow({ ...newRow, unit_name: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={addNew} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium">Save</button>
            <button onClick={() => setNewRow(null)} className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded text-sm font-medium">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow divide-y divide-gray-100">
        {mappings.length === 0 ? (
          <div className="p-6 text-center text-gray-500 text-sm">
            No mappings yet. Upload a CryptoPay file — if it contains any unrecognized swipers, you'll be prompted to map them there.
          </div>
        ) : (
          mappings.map((m) => (
            <div key={m.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="text-sm font-mono text-gray-900 sm:w-40 min-w-0 truncate">{m.swiper_id}</div>
              <div className="text-gray-400 hidden sm:block">→</div>
              <select
                value={m.unit_name}
                onChange={(e) => updateRow(m.id, { unit_name: e.target.value })}
                className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                {/* If the current value isn't in the equipment list (e.g. old
                    mapping from before an equipment count changed) preserve it. */}
                {!options.find((o) => o.value === m.unit_name) && (
                  <option value={m.unit_name}>{m.unit_name}</option>
                )}
              </select>
              <label className="flex items-center gap-1.5 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={m.active}
                  onChange={(e) => updateRow(m.id, { active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                active
              </label>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => saveRow(m)}
                  disabled={savingIds.has(m.id)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium disabled:opacity-50"
                >
                  {savingIds.has(m.id) ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => removeRow(m)}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Settings;
